import { useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ImageUpload } from "@/components/ui/image-upload";
import { supabase } from "@/integrations/supabase/client";
import { uploadImageToS3Optimized } from "@/services/imageService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

interface ProjectData {
  title: string;
  storage_path: string;
  order_num: number;
  media_count: number;
}

export default function NewHomeProject() {
  const [title, setTitle] = useState({
    tr: "",
    en: "",
    ar: ""
  });
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) {
      toast({
        title: "Hata",
        description: "Lütfen bir görsel seçin.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get the highest order_num
      const { data: existingProjects, error: fetchError } = await supabase
        .from('home_projects')
        .select('order_num')
        .order('order_num', { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;

      // Calculate new order_num
      const newOrderNum = existingProjects && existingProjects.length > 0 
        ? existingProjects[0].order_num + 1 
        : 1;

      // Upload optimized image to S3
      const imageUrl = await uploadImageToS3Optimized(image, 'home-projects');

      // Save project to Supabase
      const projectData: ProjectData = {
        title: JSON.stringify(title),
        storage_path: imageUrl,
        order_num: newOrderNum,
        media_count: 1
      };

      const { error } = await supabase
        .from('home_projects')
        .insert(projectData);

      if (error) throw error;

      toast({
        title: "Başarılı",
        description: "Yeni proje başarıyla eklendi.",
      });
      
      navigate("/content/home/projects");
    } catch (error) {
      console.error('Error saving project:', error);
      toast({
        title: "Hata",
        description: "Proje eklenirken bir hata oluştu.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentLayout 
      title="Yeni Anasayfa Projesi Ekle" 
      backLink="/content/home/projects"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Tabs defaultValue="tr" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="tr">{languageFlags.tr} Türkçe</TabsTrigger>
                <TabsTrigger value="en">{languageFlags.en} English</TabsTrigger>
                <TabsTrigger value="ar">{languageFlags.ar} العربية</TabsTrigger>
              </TabsList>
              <TabsContent value="tr" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title-tr">Başlık (Türkçe)</Label>
                  <Input
                    id="title-tr"
                    placeholder="Proje başlığı"
                    value={title.tr}
                    onChange={(e) => setTitle({ ...title, tr: e.target.value })}
                    required
                  />
                </div>
              </TabsContent>
              <TabsContent value="en" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title-en">Title (English)</Label>
                  <Input
                    id="title-en"
                    placeholder="Project title"
                    value={title.en}
                    onChange={(e) => setTitle({ ...title, en: e.target.value })}
                    required
                  />
                </div>
              </TabsContent>
              <TabsContent value="ar" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title-ar">العنوان (العربية)</Label>
                  <Input
                    id="title-ar"
                    placeholder="عنوان المشروع"
                    value={title.ar}
                    onChange={(e) => setTitle({ ...title, ar: e.target.value })}
                    required
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
          
          <div>
            <Card>
              <CardContent className="p-6">
                <Label>Proje Görseli</Label>
                <ImageUpload
                  value={image}
                  onChange={setImage}
                  className="mt-2"
                />
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="flex justify-end gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate("/content/home/projects")}
          >
            İptal
          </Button>
          <Button type="submit" disabled={isSubmitting || !image}>
            {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </form>
    </ContentLayout>
  );
}
